import {
    CallingTheme,
    CreateVideoStreamViewResult,
    StreamMedia,
    VideoGalleryRemoteParticipant,
    VideoStreamOptions,
    VideoTile,
    useTheme,
} from "@azure/communication-react";
import { IContextualMenuItem, IContextualMenuProps, IContextualMenuStyles, Stack, Theme } from "@fluentui/react";
import React, { CSSProperties, useCallback, useMemo } from "react";
import { VideoTileIndicatorsOverlay } from "./VideoTileIndicatorsOverlay";
import { videoTileStyle } from "../../styles/VideoTile.styles";


const containerStyles: CSSProperties = {
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",

    // height: "100%",
    // width: "100%",
    // position: "relative",
};

const contextMenuStyles: IContextualMenuStyles = {
    root: {
        // maxWidth: '999px',
    },
    title: {},
    header: {},
    list: {},
    container: {
        maxWidth: "fit-content",
    },
    subComponentStyles: {
        menuItem: [],
        callout: [],
    }
}

const videoStyles = { root: { '& video': { borderRadius: '0rem' } } };


interface RemoteVideoTileProps {
    remoteParticipant: VideoGalleryRemoteParticipant;
    menuItems: IContextualMenuItem[];

    // total number of all users in the call (moderators, students, local participant)
    numberOfParticipants?: number;
    isFirstPresenter?: boolean;

    createRemoteStreamView:  (userId: string, options?: VideoStreamOptions) => Promise<void | CreateVideoStreamViewResult>;
    disposeRemoteVideoStreamView(userId: string): void;
}

export const RemoteVideoTile = (props: RemoteVideoTileProps): JSX.Element => {
    const {
        remoteParticipant,
        numberOfParticipants,
        isFirstPresenter,
        createRemoteStreamView,
        disposeRemoteVideoStreamView,
    } = props;

    const renderElement = remoteParticipant?.videoStream?.renderElement;
    const isAvailable = remoteParticipant?.videoStream?.isAvailable;
    const isReceiving = remoteParticipant?.videoStream?.isReceiving;
    const showLoadingIndicator = isAvailable && isReceiving === false && remoteParticipant?.state !== 'Disconnected';
    const themeV8 = useTheme() as Theme & CallingTheme;   //using value of "FluentThemeProvider" component

    const renderVideoStreamElement = useMemo(() => {
        createRemoteStreamView(remoteParticipant.userId, { scalingMode: 'Fit' });

        // Checking if renderElement is well defined or not as calling SDK has a number of video streams limitation which
        // implies that, after their threshold, all streams have no child (blank video)
        if (!renderElement || !renderElement.childElementCount) {
            // Returning `undefined` results in the placeholder with avatar being shown
            return undefined;
        }

        // isAvailable === false => video is turned off
        // isAvailable === true && isReceiving === false => video is turned on but not receiving any video (loading)

        // don't show any stream media when video is turned off. This will show a placeholder with avatar instead.
        if (!isAvailable) {
            return undefined;
        }

        return (
            <StreamMedia
                styles={videoStyles}
                videoStreamElement={renderElement!}
                loadingState={showLoadingIndicator ? 'loading' : 'none'}
            />
        );
    }, [createRemoteStreamView, remoteParticipant.userId, renderElement, isAvailable, showLoadingIndicator]);

    const remoteVideoTileStyle = videoTileStyle(themeV8, !!remoteParticipant.raisedHand);

    const contextualMenu = useMemo(() => ({
        items: props.menuItems,
        key: remoteParticipant.userId,
        styles: contextMenuStyles,
    } as IContextualMenuProps), [props.menuItems, remoteParticipant.userId]);

    const openContextMenu = useCallback((event: React.MouseEvent<HTMLElement, MouseEvent>) => {
        // It's not possible to reference the more menu button the React way.
        // As a workaround, we select the DOM element using querySelector and trigger a new Click event on it.
        const moreMenuBtn = event.currentTarget.querySelector('[data-ui-id="video-tile-more-options-button"]');

        // NOTE: would be nice to detect if the menu is already open and prevent opening it again.
        // The DOM element has an attribute `aria-expanded=true` when opened,
        // but it gets closed immediately after the click event / before this callback is executed.
        // => not possible to detect it this way
        moreMenuBtn?.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, composed: true }));
    }, []);

    return (
        <Stack
            style={containerStyles}
            key={remoteParticipant.userId}
            onClick={openContextMenu}
        >
            {/* Debug remote vide state */}
            {/*isAvailable: {isAvailable ? "true" : "false"} <br />*/}
            {/*isReceiving: {isReceiving ? "true" : "false"} <br />*/}
            {/*participantState: {remoteParticipant?.state} <br />*/}
            {/*isSpeaking: {remoteParticipant.isSpeaking ? "true" : "false"} <br />*/}
            {/*isMuted: {remoteParticipant.isMuted ? "true" : "false"} <br />*/}
            {/*<div>state = {remoteParticipant.state}</div>*/}
            <VideoTile
                {...remoteParticipant}
                contextualMenu={contextualMenu}
                key={remoteParticipant.userId}
                userId={remoteParticipant.userId}
                renderElement={renderVideoStreamElement}
                raisedHand={undefined}
                styles={remoteVideoTileStyle}
                showMuteIndicator={false}
            >
                <VideoTileIndicatorsOverlay
                    isMuted={remoteParticipant.isMuted}
                    raisedHand={remoteParticipant.raisedHand}
                    isSpeaking={remoteParticipant.isSpeaking}
                    numberOfParticipants={isFirstPresenter ? numberOfParticipants : undefined}
                />
            </VideoTile>
        </Stack>
    );
}
